import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import AnalysisTypeSelector from './AnalysisTypeSelector';
import AnalyzeButton from './AnalyzeButton';
import FileUpload from './FileUpload';
import { useWebsiteAnalysis } from '@/hooks/useWebsiteAnalysis';
import { useProfile } from '@/hooks/useProfile';
import { convertToCSV, downloadCSV } from '@/utils/csvHelpers';

export default function WebsiteClassifier() {
    const { user } = useAuth();
    const [file, setFile] = useState(null);
    const [analysisName, setAnalysisName] = useState('');
    const [isDualAnalysis, setIsDualAnalysis] = useState(false);
    const [selectedTypes, setSelectedTypes] = useState({
        primary: '',
        secondary: ''
    });

    const {
        isAnalyzing,
        error,
        setError,
        analysisTypes,
        isLoading,
        analyzeWebsites
    } = useWebsiteAnalysis(user);

    const { fetchProfile } = useProfile();

    const handleAnalyze = async () => {
        if (!file) {
            setError('Please upload a CSV file first');
            return;
        }

        if (!selectedTypes.primary || (isDualAnalysis && !selectedTypes.secondary)) {
            setError('Please select all required analysis types');
            return;
        }

        try {
            // Pass the analysis name to the analyzeWebsites function
            const results = await analyzeWebsites(file, selectedTypes, isDualAnalysis, analysisName);
            const csvContent = convertToCSV(results);
            downloadCSV(csvContent);
            await fetchProfile(); // Refresh profile data including credits
        } catch (err) {
            // Error is already handled in the hook
        }
    };

    return (
        <div className="space-y-8">
            <AnalysisTypeSelector
                isDualAnalysis={isDualAnalysis}
                setIsDualAnalysis={setIsDualAnalysis}
                selectedTypes={selectedTypes}
                setSelectedTypes={setSelectedTypes}
                analysisTypes={analysisTypes}
                isAnalyzing={isAnalyzing}
                isLoading={isLoading}
            />

            <FileUpload
                file={file}
                setFile={setFile}
                setError={setError}
                isAnalyzing={isAnalyzing}
                isLoading={isLoading}
                analysisName={analysisName}
                setAnalysisName={setAnalysisName}
            />

            {error && (
                <div className="rounded-md bg-destructive/15 p-3">
                    <p className="text-sm text-destructive">{error}</p>
                </div>
            )}

            <div className="flex justify-end gap-3">
                <AnalyzeButton
                    handleAnalyze={handleAnalyze}
                    isAnalyzing={isAnalyzing}
                    file={file}
                    selectedTypes={selectedTypes}
                    isDualAnalysis={isDualAnalysis}
                    isLoading={isLoading}
                />
            </div>
        </div>
    );
}